import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchMyClasses, fetchStudentSystemSettings } from '../../services/classService';
import { createLogger } from '../../utils/logger';

const logger = createLogger('useClassNav');

// Resolved answers. Every route mounts its own <Layout>, so the sidebar remounts on each
// navigation; without a cache the links would flicker on every page. Keyed by user, role
// and org, because the demo proxy ("view as") changes the last two under the same user,
// and kept for a few minutes so someone added to a class sees My Classes without a reload.
const TTL_MS = 5 * 60 * 1000;
const enabledFor = new Map<string, { value: boolean; at: number }>();
const hasClassesFor = new Map<string, { value: boolean; at: number }>();
const isStudentFor = new Map<string, { value: boolean; at: number }>();

const fresh = (cache: Map<string, { value: boolean; at: number }>, key: string): boolean | undefined => {
  const hit = cache.get(key);
  return hit && Date.now() - hit.at < TTL_MS ? hit.value : undefined;
};

const cacheKey = (userId: string, role: string | null | undefined, orgId: string | null | undefined) =>
  `${userId}:${role ?? ''}:${orgId ?? ''}`;

/** The Classes page turned the student system on or off: show or hide the link on the
 *  next sidebar mount without waiting for a reload. */
export function recordStudentSystemEnabled(
  userId: string | null | undefined,
  role: string | null | undefined,
  orgId: string | null | undefined,
  enabled: boolean
): void {
  if (userId) enabledFor.set(cacheKey(userId, role, orgId), { value: enabled, at: Date.now() });
}

/** Test-only: forget cached answers between cases. */
export function resetClassNavCache(): void {
  enabledFor.clear();
  hasClassesFor.clear();
  isStudentFor.clear();
}

export interface ClassNav {
  /** "Classes" (school admin): a super admin always; an org admin once their school uses the student system. */
  showClasses: boolean;
  /** "My Classes": anyone who teaches or is a student in at least one class. */
  showMyClasses: boolean;
  /** "My Practice" (student home): anyone who is a student in at least one class. */
  showStudentHome: boolean;
}

/**
 * Which student-system links the sidebar shows. Adult-training orgs never turn the
 * student system on, so their admins and members see no new links at all.
 *
 * Both answers start hidden and appear once known; a failed lookup keeps them hidden,
 * since neither page has anything to show someone outside the student system.
 */
export function useClassNav(): ClassNav {
  const { currentUser, userRole } = useAuth();
  const userId = currentUser?.id ?? '';
  const key = userId ? cacheKey(userId, userRole, currentUser?.organization_id) : '';
  const isAdmin = userRole === 'admin';
  const wantsMyClasses = Boolean(userId) && userRole !== 'super_admin';
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const settle = () => {
      if (!cancelled) setVersion(v => v + 1);
    };
    if (isAdmin && fresh(enabledFor, key) === undefined) {
      fetchStudentSystemSettings()
        .then(s => enabledFor.set(key, { value: s.student_system_enabled, at: Date.now() }))
        .catch(err => logger.warn('Could not read student system settings', err))
        .finally(settle);
    }
    if (wantsMyClasses && fresh(hasClassesFor, key) === undefined) {
      fetchMyClasses()
        .then(mine => {
          const at = Date.now();
          hasClassesFor.set(key, { value: mine.classes.length > 0, at });
          isStudentFor.set(key, { value: mine.classes.some(c => c.role === 'student'), at });
        })
        .catch(err => logger.warn('Could not load my classes', err))
        .finally(settle);
    }
    return () => {
      cancelled = true;
    };
  }, [key, isAdmin, wantsMyClasses]);

  // A stale answer keeps showing until the refetch above replaces it, rather than
  // hiding the link for a moment on every navigation.
  return {
    showClasses: userRole === 'super_admin' || (isAdmin && enabledFor.get(key)?.value === true),
    showMyClasses: wantsMyClasses && hasClassesFor.get(key)?.value === true,
    showStudentHome: wantsMyClasses && isStudentFor.get(key)?.value === true,
  };
}
